import React, { useEffect, useMemo, useState } from 'react';
import { LuCalendarClock, LuCircleAlert, LuCircleCheck, LuClock, LuChevronDown, LuListChecks } from 'react-icons/lu';
import api from '../../services/api';
import { Tildable, BarraSeleccion } from '../ui/Seleccion';
import { useSeleccion } from '../../hooks/useSeleccion';
import { esAbierto } from '../../utils/pedidosEmpresa';

// Próximos mantenimientos (Portal Empresa, 8-oct-2026): para cada equipo, cuándo le
// toca la sanitización (cada 6 meses) o el cambio de filtro (cada 12), desde su
// última visita. Le llega además un aviso 15 días antes de que venza.
// 13-oct-2026: agrupado por cliente (un "Pedir" por lugar con todos sus equipos), "Al día" plegado
// y, si ese lugar ya tiene un pedido en curso, lo dice en vez de ofrecer pedir otro.
const fmt = (iso) => { if (!iso) return ''; const [, m, d] = String(iso).slice(0, 10).split('-'); return `${d}/${m}`; };
const TIPO = { FILTRO: 'Cambio de filtro', SANITIZACION: 'Sanitización' };
const GRUPOS = [
    { id: 'VENCIDO', titulo: 'Vencidos', icono: LuCircleAlert, color: '#C9341F' },
    { id: 'PRONTO', titulo: 'Próximos 30 días', icono: LuClock, color: '#D48800' },
    { id: 'AL_DIA', titulo: 'Al día', icono: LuCircleCheck, color: '#16A34A', plegado: true },
    { id: 'SIN_DATOS', titulo: 'Sin visitas registradas', icono: LuCalendarClock, color: '#78716C', plegado: true },
];

const cuando = (m) => {
    if (m.dias == null) return 'Todavía no lo atendimos';
    if (m.dias < 0) return `Venció hace ${-m.dias} día${m.dias === -1 ? '' : 's'}`;
    if (m.dias === 0) return 'Vence hoy';
    return `Vence en ${m.dias} día${m.dias === 1 ? '' : 's'} (${fmt(m.vence)})`;
};

// Un renglón por lugar: sus equipos de ese grupo, el más urgente manda
function porLugar(items) {
    const m = new Map();
    items.forEach(e => {
        const k = e.sedeId ?? e.sede ?? e.direccion;
        if (!m.has(k)) m.set(k, { clave: String(k), sedeId: e.sedeId, nombre: e.sede || e.direccion || '—', equipos: [] });
        m.get(k).equipos.push(e);
    });
    return [...m.values()].map(l => {
        const peor = l.equipos.reduce((a, b) => ((b.dias ?? 1e9) < (a.dias ?? 1e9) ? b : a));
        const tipos = [...new Set(l.equipos.map(e => TIPO[e.tipo]).filter(Boolean))];
        return { ...l, peor, tipos };
    }).sort((a, b) => (a.peor.dias ?? 1e9) - (b.peor.dias ?? 1e9) || a.nombre.localeCompare(b.nombre, 'es'));
}

export default function MantenimientosEmpresa({ onPedir, pedidos = [] }) {
    const [lista, setLista] = useState(null);
    const [abiertos, setAbiertos] = useState({});
    useEffect(() => { api.get('/empresa/mantenimientos').then(r => setLista(Array.isArray(r.data) ? r.data : [])).catch(() => setLista([])); }, []);
    const enCurso = useMemo(() => new Set(pedidos.filter(p => !p.archivado && esAbierto(p)).map(p => p.sedeId)), [pedidos]);
    const grupos = useMemo(() => GRUPOS.map(g => {
        const items = (lista || []).filter(m => m.estado === g.id);
        return { ...g, equipos: items.length, lugares: porLugar(items) };
    }).filter(g => g.equipos), [lista]);
    // Varios a la vez (11-oct-2026): se tildan lugares y se pide el service de todos juntos
    const s = useSeleccion();
    const elegibles = grupos.filter(g => !g.plegado).flatMap(g => g.lugares);

    if (!lista) return <p className="py-16 text-center text-muted text-body">Cargando…</p>;
    if (!lista.length) return <p className="py-16 text-center text-body text-muted">Todavía no hay equipos cargados.</p>;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
                {GRUPOS.slice(0, 3).map(g => {
                    const n = (lista || []).filter(m => m.estado === g.id).length;
                    return (
                        <div key={g.id} className="px-3 py-2.5 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06]">
                            <p className="flex items-center gap-1 text-[11px] font-black uppercase text-muted truncate"><g.icono size={12} className="shrink-0" style={{ color: g.color }} />{g.id === 'PRONTO' ? 'Próximos' : g.titulo}</p>
                            <p className="text-xl font-black text-ink">{n} <span className="text-caption font-bold text-muted">eq.</span></p>
                        </div>
                    );
                })}
            </div>
            <p className="text-caption text-muted px-1">Sanitización cada 6 meses y cambio de filtro cada 12, desde la última visita. Te avisamos 15 días antes.</p>
            {grupos.map((g, gi) => {
                const plegado = g.plegado && !abiertos[g.id];
                return (
                    <section key={g.id} className="space-y-1.5">
                        <div className="flex items-center gap-2 px-1 min-h-[28px]">
                            <p className="flex-1 min-w-0 flex items-center gap-1.5 text-label font-black uppercase tracking-wider truncate" style={{ color: g.color }}>
                                <g.icono size={14} className="shrink-0" /><span className="truncate">{g.titulo} · {g.equipos} eq.</span>
                                <span className="hidden sm:inline text-muted normal-case tracking-normal font-bold">en {g.lugares.length} lugar{g.lugares.length !== 1 ? 'es' : ''}</span>
                            </p>
                            {gi === 0 && !g.plegado && elegibles.length > 1 && (
                                <button type="button" onClick={() => (s.activo ? s.salir() : s.entrar())} aria-pressed={s.activo}
                                    className={`shrink-0 h-7 px-2.5 rounded-lg inline-flex items-center gap-1 text-label font-black ${s.activo ? 'bg-ink text-page' : 'text-secondary'}`}>
                                    <LuListChecks size={14} /> {s.activo ? 'Listo' : 'Seleccionar'}
                                </button>
                            )}
                        </div>
                        {plegado ? (
                            <button type="button" onClick={() => setAbiertos(a => ({ ...a, [g.id]: true }))}
                                className="w-full h-11 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] text-label font-black text-secondary inline-flex items-center justify-center gap-1.5">
                                Ver los {g.lugares.length} lugares <LuChevronDown size={15} />
                            </button>
                        ) : (
                            <div className={s.activo && !g.plegado ? 'space-y-1.5' : 'rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] divide-y divide-black/[0.06] dark:divide-white/[0.06] overflow-hidden'}>
                                {g.lugares.map(l => {
                                    const yaPedido = enCurso.has(l.sedeId);
                                    return (
                                        <Tildable key={l.clave} activo={s.activo && !g.plegado} tildado={s.tiene(l.clave)} onAlternar={() => s.alternar(l.clave)}>
                                            <div className={`flex items-center gap-3 px-4 py-3 ${s.activo && !g.plegado ? 'rounded-2xl bg-card' : ''}`}>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-body font-black text-ink truncate">{l.nombre}</p>
                                                    <p className="text-caption text-muted truncate">
                                                        {l.equipos.length} equipo{l.equipos.length !== 1 ? 's' : ''}{l.tipos.length ? ` · ${l.tipos.join(' y ')}` : ''} · {l.equipos.map(e => e.serie).join(', ')}
                                                    </p>
                                                    <p className="text-label font-bold" style={{ color: g.plegado ? undefined : g.color }}>{cuando(l.peor)}</p>
                                                </div>
                                                {!g.plegado && !s.activo && (yaPedido ? (
                                                    <span className="shrink-0 h-7 px-2.5 rounded-full bg-[rgba(37,99,235,0.12)] text-[#2563EB] dark:text-[#60A5FA] text-label font-black inline-flex items-center">Pedido en curso</span>
                                                ) : (
                                                    <button type="button" onClick={() => onPedir(l.equipos)}
                                                        className="shrink-0 h-9 px-3 rounded-xl bg-[#C9341F] text-white text-label font-black active:scale-95">Pedir</button>
                                                ))}
                                            </div>
                                        </Tildable>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                );
            })}
            {s.activo && <div className="h-32" />}
            {s.activo && (
                <BarraSeleccion cantidad={s.cantidad} total={elegibles.length} onTodos={() => s.todos(elegibles.map(l => l.clave))}
                    onSalir={s.salir} acciones={[{ label: 'Pedir service', peligro: true,
                        onClick: () => { onPedir(elegibles.filter(l => s.tiene(l.clave)).flatMap(l => l.equipos)); s.salir(); } }]} />
            )}
        </div>
    );
}
